import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    LinearProgress,
    IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api/axios';

interface FileUploadDialogProps {
    open: boolean;
    onClose: () => void;
    patientId: string;  // This is the local ID from the URL
    onUploadComplete: (data?: any) => void;
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
    open,
    onClose,
    patientId,  // This is already the local ID from the URL
    onUploadComplete
}) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            setSelectedFiles(Array.from(files));
            setError('');
        }
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setError('Please select at least one file');
            return;
        }

        setUploading(true);
        setError('');

        try {
            // Upload files sequentially to avoid overwhelming the server
            const uploadResults = [];
            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                
                // Create FormData object for file upload
                const formData = new FormData();
                formData.append('file', file);
                formData.append('description', description);
                formData.append('patientId', patientId);

                // Upload using FormData (multipart/form-data)
                const response = await api.post('/api/patient-attachments', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                uploadResults.push(response.data);
            }

            // Call onUploadComplete with all upload results
            onUploadComplete({ multiple: true, uploads: uploadResults });
            handleClose();
        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.response?.data?.error || 'Failed to upload files');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFiles([]);
        setDescription('');
        setError('');
        setUploading(false);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    Upload Attachments
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                        multiple
                    />
                    <Box
                        sx={{
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography>
                            {selectedFiles.length > 0 
                                ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected`
                                : 'Click to select files'
                            }
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            Note: Files will be stored in Hydralyfe. Patient must have a Hydralyfe ID.
                        </Typography>
                    </Box>

                    {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Selected Files:
                            </Typography>
                            {selectedFiles.map((file, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        p: 1,
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                        mb: 1
                                    }}
                                >
                                    <Typography variant="body2">{file.name}</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        sx={{ mt: 2 }}
                        disabled={uploading}
                    />

                    {error && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}

                    {uploading && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={selectedFiles.length === 0 || uploading}
                >
                    {uploading ? 'Uploading...' : `Upload ${selectedFiles.length > 1 ? `${selectedFiles.length} Files` : 'File'}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileUploadDialog; 